import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Alert,
  Paper,
  Stack,
  alpha,
} from "@mui/material";
import { validateInputs } from "../utils/validation";
import { styleForTextField } from "../../../../../utils/TextField/TextField";
import CustomDatePicker from "../../../../../utils/DatePicker/CustomDatePicker";
import {
  LIMITATION_TYPES,
  isCleanValue,
} from "../../../../../../utils/static/limitation";
import { JustInfo } from "../../../../../utils/Icon";

const emptyFormData = {
  assetCost: "",
  deposit: "",
  tenor: "",
  instalmentAmount: "",
  firstInstalmentDate: null,
  inAdvance: false,
};

const mapSavedToFormData = (saved) => ({
  assetCost: saved.assetCost ?? "",
  deposit:
    saved.deposit === 0 || saved.deposit === "" || saved.deposit == null
      ? ""
      : saved.deposit,
  tenor: saved.tenor ?? "",
  instalmentAmount: saved.instalmentAmount ?? "",
  firstInstalmentDate: saved.firstInstalmentDate ?? null,
  inAdvance: Boolean(saved.inAdvance),
});

const RepaymentForm = ({
  onSubmit,
  calculationError,
  setCalculationError,
  savedStep1Inputs,
  onClearSaved,
}) => {
  const [formData, setFormData] = useState(() =>
    savedStep1Inputs
      ? mapSavedToFormData(savedStep1Inputs)
      : { ...emptyFormData },
  );

  const [errors, setErrors] = useState([]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle number fields with validation
    if (["assetCost", "tenor"].includes(name)) {
      if (isCleanValue(value, LIMITATION_TYPES.ONLY_NUMBERS)) {
        setFormData((prev) => ({
          ...prev,
          [name]: value === "" ? "" : parseFloat(value) || "",
        }));
      }
    } else if (["deposit", "instalmentAmount"].includes(name)) {
      // Allow only up to two decimals for deposit
      const twoDecimalRegex = /^\d*(\.\d{0,2})?$/;
      if (twoDecimalRegex.test(value)) {
        setFormData((prev) => ({
          ...prev,
          [name]: value === "" ? "" : parseFloat(value) || "",
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      firstInstalmentDate: date,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCalculationError(null);

    // Convert date to Date object and handle optional deposit
    const inputs = {
      ...formData,
      deposit: formData.deposit === "" ? 0 : parseFloat(formData.deposit) || 0,
      firstInstalmentDate: formData.firstInstalmentDate,
    };

    const validation = validateInputs(inputs);

    if (!validation.valid) {
      setErrors(validation.issues);
      return;
    }

    setErrors([]);

    onSubmit(inputs);
  };

  const handleClear = () => {
    setFormData({ ...emptyFormData });
    setErrors([]);
    setCalculationError(null);
    if (onClearSaved) onClearSaved();
  };

  const isFormValid = () => {
    const {
      assetCost,
      tenor,
      instalmentAmount,
      firstInstalmentDate,
      inAdvance,
    } = formData;
    return (
      assetCost !== "" &&
      tenor !== "" &&
      instalmentAmount !== "" &&
      firstInstalmentDate &&
      inAdvance !== undefined &&
      inAdvance !== null
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "," || e.key === "e" || e.key === "-" || e.key === "+") {
      e.preventDefault();
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.01),
      }}
    >
      <Stack spacing={4}>
        {(calculationError || errors.length > 0) && (
          <Alert severity='error' sx={{ borderRadius: 2 }}>
            <Typography variant='subtitle2' gutterBottom>
              <strong>
                {calculationError
                  ? "Error Generating Schedule"
                  : "Please check the following errors:"}
              </strong>
            </Typography>
            <ul>
              {calculationError ||
                errors.map((error, index) => <li key={index}>{error}</li>)}
            </ul>
          </Alert>
        )}

        <Box component='form' onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack direction='row' spacing={2}>
              <TextField
                label='Total Asset Cost (excluding interest)'
                name='assetCost'
                type='number'
                value={formData.assetCost}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                required
                fullWidth
                autoComplete='off'
                error={errors.some((e) => e.includes("asset cost"))}
                helperText={
                  errors.some((e) => e.includes("asset cost"))
                    ? "Please enter a valid asset cost or check other fields"
                    : "Cost of asset, excluding interest"
                }
                InputProps={{
                  startAdornment: (
                    <Typography
                      variant='body1'
                      color='text.secondary'
                      sx={{ mr: 1 }}
                    >
                      $
                    </Typography>
                  ),
                  inputProps: {
                    min: 0,
                    step: "0.01",
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  },
                  "aria-label": "Total Asset Cost",
                }}
                sx={styleForTextField}
              />

              <TextField
                label='Deposit Paid (Optional)'
                name='deposit'
                type='number'
                value={formData.deposit}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                fullWidth
                autoComplete='off'
                placeholder='0.00'
                error={errors.some((e) => e.includes("Deposit"))}
                helperText={
                  errors.some((e) => e.includes("asset cost"))
                    ? "Please enter a valid deposit amount or check other fields"
                    : "Optional deposit amount paid upfront"
                }
                InputProps={{
                  startAdornment: (
                    <Typography
                      variant='body1'
                      color='text.secondary'
                      sx={{ mr: 1 }}
                    >
                      $
                    </Typography>
                  ),
                  inputProps: {
                    min: 0,
                    step: "0.01",
                    inputMode: "decimal",
                    pattern: "^\\d*(\\.\\d{0,2})?$",
                    maxLength: 15,
                  },
                  "aria-label": "Deposit Paid",
                }}
                sx={styleForTextField}
              />
            </Stack>

            <TextField
              label='Tenor (Number of Instalments)'
              name='tenor'
              type='number'
              value={formData.tenor}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              required
              fullWidth
              autoComplete='off'
              error={errors.some((e) => e.includes("Tenor"))}
              helperText={
                errors.some((e) => e.includes("Tenor"))
                  ? errors.find((e) => e.includes("Tenor"))
                  : "Total number of instalment payments"
              }
              InputProps={{
                inputProps: {
                  min: 1,
                  step: "1",
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                },
                "aria-label": "Tenor",
              }}
              sx={styleForTextField}
            />

            <TextField
              label='Instalment Amount (PMT)'
              name='instalmentAmount'
              type='number'
              value={formData.instalmentAmount}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              required
              fullWidth
              placeholder='0.00'
              autoComplete='off'
              error={errors.some((e) => e.includes("Instalment amount"))}
              helperText={
                errors.some((e) => e.includes("Instalment amount"))
                  ? errors.find((e) => e.includes("Instalment amount"))
                  : "Fixed instalment payment amount"
              }
              InputProps={{
                startAdornment: (
                  <Typography
                    variant='body1'
                    color='text.secondary'
                    sx={{ mr: 1 }}
                  >
                    $
                  </Typography>
                ),
                inputProps: {
                  min: 0,
                  step: "0.01",
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                },
                "aria-label": "Instalment Amount",
              }}
              sx={styleForTextField}
            />

            <CustomDatePicker
              label='Date of First Instalment'
              selected={formData.firstInstalmentDate}
              onChange={handleDateChange}
              dateFormat='dd-MM-yyyy'
              fullWidth
              error={errors.some((e) => e.includes("first instalment"))}
              helperText={
                errors.some((e) => e.includes("first instalment"))
                  ? errors.find((e) => e.includes("first instalment"))
                  : "Date when the first instalment is due"
              }
            />

            <FormControl component='fieldset'>
              <FormLabel
                component='legend'
                sx={{
                  fontWeight: 500,
                  letterSpacing: 0.3,
                  color: "text.secondary",
                  mb: 1.5,
                  "&.Mui-focused": {
                    color: "primary.main",
                  },
                }}
              >
                Payment Timing *
              </FormLabel>
              <RadioGroup
                row
                name='inAdvance'
                value={formData.inAdvance}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    inAdvance: e.target.value === "true",
                  }))
                }
                sx={{
                  "& .MuiFormControlLabel-root": {
                    mr: 3,
                  },
                }}
              >
                <FormControlLabel
                  value='false'
                  control={<Radio />}
                  label={
                    <Box
                      component='span'
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      In Arrears
                      <JustInfo
                        title='Deposit paid at start of contract and 1st instalment paid 1 period after'
                        ml={0.5}
                        mr={0}
                      />
                    </Box>
                  }
                />
                <FormControlLabel
                  value='true'
                  control={<Radio />}
                  label={
                    <Box
                      component='span'
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      In Advance
                      <JustInfo
                        title='Deposit and 1st instalment paid at the start of contract'
                        ml={0.5}
                        mr={0}
                      />
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            <Stack direction='row' spacing={2}>
              <Button
                type='submit'
                variant='contained'
                color='primary'
                disabled={!isFormValid()}
                sx={{ minWidth: 200 }}
              >
                Generate HP Repayment Schedule
              </Button>
              <Button
                type='button'
                variant='outlined'
                color='secondary'
                onClick={handleClear}
              >
                Clear All
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};

export default RepaymentForm;
